import { Badge } from '@neo/primitives/badge'
import { Button } from '@neo/primitives/button'
import { Tabs, TabsList, TabsTrigger } from '@neo/primitives/tabs'
import { Typography } from '@neo/primitives/typography'
import { Check, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import type { RSandboxNumber, RSandboxSignupNumber } from 'v2/types/api/settings'
import { getSandboxNumberDisplay } from './sandbox-number-display'
import {
  getAddLeftLabel,
  getCallableNumbers,
  getCreditsLabel,
  getDirectionCopy,
  type SandboxConnectKind,
  SandboxDirection,
} from './sandbox-setup-copy'

export interface SandboxSetupPanelProps {
  /** The account's trial DID. Absent until AMS assigns one at signup. */
  sandboxNumber?: string
  /** Formatted balance, e.g. "₹210". */
  creditBalance?: string
  /** Formatted trial grant, e.g. "₹250". Omitted until something can supply it. */
  creditGrantTotal?: string
  verified: RSandboxNumber[]
  signupNumber?: RSandboxSignupNumber
  cap?: number
  /** Concurrency unlocked after compliance, for the KYC banner. */
  unlockedConcurrency: number
  /** Credit granted on approval, e.g. "₹1,000". Omitted when unknown. */
  approvalCredit?: string
  /**
   * Whether to offer the compliance CTA. India-only: the documents, the credit
   * and the route are all India-specific, so a US trial account would get a
   * dead link. Same rule the Phone Numbers comparison uses.
   */
  showsCompliance?: boolean
  onManageNumbers: () => void
  onAddNumber: () => void
  onChooseConnect: (kind: SandboxConnectKind) => void
  /**
   * Which connect options this role may actually reach. An option omitted here
   * is not rendered: the destinations carry their own RBAC, and offering a
   * choice that bounces off a forbidden page is worse than not offering it.
   */
  allowedConnectKinds?: SandboxConnectKind[]
  /** What is currently attached to the sandbox number, so step 2 shows its state. */
  selectedConnectKind?: SandboxConnectKind
  onStartKyc: () => void
  onCompare: () => void
  /**
   * Places the outbound test call. Optional, and its ABSENCE disables the
   * control rather than rendering an enabled button that does nothing.
   */
  onPlaceCall?: () => void
}

const StepCard = ({
  index,
  title,
  subtitle,
  children,
  action,
  isComplete,
}: {
  index: number
  title: string
  subtitle?: string
  children?: React.ReactNode
  action?: React.ReactNode
  isComplete?: boolean
}) => (
  <div className="flex flex-col gap-3 rounded-lg border p-4">
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        {/* Completed steps go green. Same colours as Badge variant="success"
            (bg-emerald-100/text-emerald-950); there is no shared stepper
            primitive in neo, so each surface spells this out. */}
        <span
          className={
            isComplete
              ? 'flex h-6 w-6 flex-none items-center justify-center rounded-md bg-emerald-100 text-emerald-950 text-xxs-600'
              : 'flex h-6 w-6 flex-none items-center justify-center rounded-md bg-bg3 text-xxs-600'
          }
          data-testid={`badge-sandbox-step-${index}`}
          data-complete={isComplete ? 'true' : 'false'}
        >
          {isComplete ? <Check className="h-3.5 w-3.5" /> : index}
        </span>
        <div className="flex flex-col gap-1">
          <Typography className="text-p-600">{title}</Typography>
          {subtitle && <Typography className="text-s-400 text-secondary">{subtitle}</Typography>}
        </div>
      </div>
      {action}
    </div>
    {children}
  </div>
)

/**
 * "Test your calling setup" - the guided sandbox flow from the v2 design.
 *
 * Prop-driven so every state is renderable in a test: the wired parent reads
 * the sandbox list, the billing balance and the plan, none of which can be
 * controlled from the full suite.
 */
export const SandboxSetupPanel = ({
  sandboxNumber,
  creditBalance,
  creditGrantTotal,
  verified,
  signupNumber,
  cap,
  unlockedConcurrency,
  approvalCredit,
  allowedConnectKinds,
  selectedConnectKind,
  showsCompliance = true,
  onManageNumbers,
  onAddNumber,
  onChooseConnect,
  onStartKyc,
  onCompare,
  onPlaceCall,
}: SandboxSetupPanelProps) => {
  const [direction, setDirection] = useState(SandboxDirection.inbound)
  const copy = getDirectionCopy(direction)
  const callable = getCallableNumbers(verified, signupNumber)
  const addLeft = getAddLeftLabel(verified.length, cap)
  const credits = getCreditsLabel(creditBalance, creditGrantTotal)
  // Resolved once rather than chained in the JSX: three static options, so the
  // point is readability at the call site, not the extra pass.
  const visibleOptions = allowedConnectKinds
    ? copy.options.filter((option) => allowedConnectKinds.includes(option.kind))
    : copy.options
  const formattedSandbox = sandboxNumber
    ? getSandboxNumberDisplay(sandboxNumber).formatted
    : undefined

  return (
    <div className="flex flex-col gap-4" data-testid="sandbox-setup-panel">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Typography className="text-h4-600">Test your calling setup</Typography>
          <Badge variant="blue">Sandbox</Badge>
        </div>
        <Typography className="text-s-400 text-secondary">
          Your sandbox includes a free Plivo number for testing. Calls can only be made to, or
          received from, your verified numbers
          {cap !== undefined ? `, you can add up to ${cap}.` : '.'}
        </Typography>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="flex items-center justify-between gap-3 rounded-lg bg-bg2 px-4 py-3">
          <div className="flex items-center gap-2">
            <Typography className="text-s-400 text-secondary">Sandbox number:</Typography>
            <Typography className="text-p-600" data-testid="text-sandbox-number">
              {formattedSandbox ?? 'Not assigned yet'}
            </Typography>
          </div>
          {formattedSandbox && <Badge variant="default">₹0 rental</Badge>}
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-bg2 px-4 py-3">
          <Typography className="text-s-400 text-secondary">Remaining credits:</Typography>
          <Typography className="text-p-600" data-testid="text-sandbox-credits">
            {credits ?? '—'}
          </Typography>
        </div>
      </div>

      <Tabs value={direction} onValueChange={(v) => setDirection(v as SandboxDirection)}>
        <TabsList>
          <TabsTrigger
            value={SandboxDirection.inbound}
            variant="simple"
            data-testid="tab-sandbox-inbound"
          >
            Inbound
          </TabsTrigger>
          <TabsTrigger
            value={SandboxDirection.outbound}
            variant="simple"
            data-testid="tab-sandbox-outbound"
          >
            Outbound
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <Typography className="text-p-600">{copy.heading}</Typography>

      <StepCard
        index={1}
        isComplete={callable.length > 0}
        title="Verify numbers that you will use for testing"
        subtitle={
          callable.length
            ? callable.map((n) => getSandboxNumberDisplay(n).formatted).join(', ')
            : undefined
        }
        action={
          <div className="flex flex-none items-center gap-3">
            {addLeft && (
              <Button
                variant="outline"
                size="sm"
                onClick={onAddNumber}
                data-testid="button-sandbox-setup-add"
              >
                {addLeft}
              </Button>
            )}
            <Button
              variant="ghostLink"
              className="px-0"
              onClick={onManageNumbers}
              data-testid="button-sandbox-setup-manage"
            >
              Manage numbers
            </Button>
          </div>
        }
      />

      <StepCard
        index={2}
        isComplete={Boolean(selectedConnectKind)}
        title={copy.chooseTitle}
        subtitle={copy.chooseSubtitle}
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {visibleOptions.map((option) => {
            const isSelected = option.kind === selectedConnectKind
            return (
              <button
                key={option.kind}
                type="button"
                onClick={() => onChooseConnect(option.kind)}
                className={
                  isSelected
                    ? 'flex gap-2 rounded-lg border border-primary bg-bg2 p-3 text-left'
                    : 'flex gap-2 rounded-lg border p-3 text-left hover:bg-hv1'
                }
                data-testid={`button-sandbox-connect-${option.kind}`}
                data-selected={isSelected ? 'true' : 'false'}
              >
                {/* Mirrors the dot the design draws on these cards. Not a real
                    radio: the card opens a picker rather than selecting a value,
                    so the state is announced as text instead of aria-checked. */}
                <span
                  aria-hidden="true"
                  className={
                    isSelected
                      ? 'mt-1 flex size-4 shrink-0 items-center justify-center rounded-full border border-primary'
                      : 'mt-1 flex size-4 shrink-0 items-center justify-center rounded-full border border-hv3'
                  }
                >
                  {isSelected && <span className="size-2 rounded-full bg-primary" />}
                </span>
                <span className="flex flex-col gap-1">
                  <Typography className="text-p-500">
                    {option.title}
                    {isSelected && <span className="sr-only"> (connected)</span>}
                  </Typography>
                  <Typography className="text-s-400 text-secondary">
                    {option.description}
                  </Typography>
                </span>
              </button>
            )
          })}
        </div>
      </StepCard>

      <StepCard index={3} title={copy.finalTitle} subtitle={copy.finalSubtitle}>
        {direction === SandboxDirection.inbound ? (
          <Typography className="text-p-500 font-mono" data-testid="text-sandbox-ring-number">
            {formattedSandbox ?? 'Your sandbox number will appear here once assigned'}
          </Typography>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Typography className="text-s-400 text-secondary">From</Typography>
            <Typography className="text-p-500 font-mono">{formattedSandbox ?? '—'}</Typography>
            <Typography className="text-s-400 text-secondary">To</Typography>
            <Typography className="text-p-500 font-mono">
              {callable[0] ? getSandboxNumberDisplay(callable[0]).formatted : '—'}
            </Typography>
            <Button
              onClick={onPlaceCall}
              disabled={!onPlaceCall || !formattedSandbox || !callable.length}
              disabledReason={
                !formattedSandbox
                  ? 'Your sandbox number has not been assigned yet'
                  : !callable.length
                    ? 'Verify a number to call first'
                    : 'Placing a call from here is not available yet'
              }
              data-testid="button-sandbox-place-call"
            >
              Place call
            </Button>
          </div>
        )}
      </StepCard>

      {showsCompliance && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-bg2 px-4 py-3">
          <div className="flex items-start gap-2">
            <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-secondary" />
            <Typography className="text-s-400 text-secondary">
              Complete KYC to call numbers outside the verified list, with up to{' '}
              {unlockedConcurrency} concurrent calls.
              {approvalCredit
                ? ` Finish it during your trial and get ${approvalCredit} in credit.`
                : ' Finish it during your trial for extra credit.'}
            </Typography>
          </div>
          <div className="flex flex-none items-center gap-3">
            <Button
              variant="ghostLink"
              className="px-0"
              onClick={onCompare}
              data-testid="button-sandbox-setup-compare"
            >
              Compare
            </Button>
            <Button onClick={onStartKyc} data-testid="button-sandbox-setup-start-kyc">
              Start KYC
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
